import { ApplicationElement, html } from '../application_element/element.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import translations from './translations.js';
import style from './style.lit.css';

/**
 * Automatically truncates content and shows more button if it's too long.
 *
 * * Big screen is greater then or equal to 768px width
 * * Small screen is less then 768px width
 *
 * @prop maxHeightLg - When we are on big screen truncate content to this height
 * @prop maxHeightSm - When we are on small screen truncate content to this height
 * @prop threshold   - Threshold in percent that needs to be broken for truncation to be activated eg. (0.5 for 50%)
 * @prop truncated - Internal property => Present if current state is truncated or not
 *
 * @cssprop [--mask-color=hex] Color of bottom background mask that goes form color to transparent
 */
export default class Truncate extends ApplicationElement {
  static styles = [ApplicationElement.styles, style];

  /** @internal */
  static translations = translations;

  static properties = {
    maxHeightLg: { type: Number },
    maxHeightSm: { type: Number },
    threshold: { type: Number },
    truncated: { type: Boolean, reflect: true },
  };

  constructor() {
    super();
    this.maxHeightLg = 300;
    this.maxHeightSm = 200;
    this.threshold = 0.25;
  }

  onClick(e) {
    e.preventDefault();
    this.truncated = !this.truncated;
  }

  shouldTruncate() {
    return this.height - this.thresholdInPixels > this.maxHeight;
  }

  setInitialState() {
    this.truncated = this.shouldTruncate();
  }

  onSlotChange() {
    this.setInitialState();
  }

  /** @internal */
  get height() {
    return this.shadowRoot.querySelector('.inner').clientHeight;
  }

  /** @internal */
  get thresholdInPixels() {
    return Math.round(this.maxHeight * this.threshold);
  }

  /** @internal */
  get maxHeight() {
    return window.innerWidth >= 768 ? this.maxHeightLg : this.maxHeightSm;
  }

  /** @internal */
  get inlineStyle() {
    if (!this.truncated) {
      return {};
    }

    return {
      maxHeight: `${this.maxHeight}px`,
    };
  }

  renderButton() {
    return html` <button @click=${this.onClick}>${this.t('more')}</button> `;
  }

  render() {
    return html`
      <div
        class=${classMap({ truncated: this.truncated })}
        style=${styleMap(this.inlineStyle)}>
        <div class="inner">
          <slot @slotchange=${this.onSlotChange}></slot>
        </div>
      </div>
      ${this.truncated ? this.renderButton() : null}
    `;
  }
}

Truncate.registerTagName('ef-truncate');

customElements.get('ef-truncate') ||
  customElements.define('ef-truncate', Truncate);
